import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { X, Star, CheckCircle } from 'lucide-react';
import { submitTrainingReview, getReviewVragen, SubmitReviewData } from '../services/opleidingenApi';
import { useAuthStore } from '../store/authStore';

interface Question {
  key: keyof Omit<SubmitReviewData, 'open_comment'>;
  label: string;
  description: string;
}

interface Props {
  trainingId: number;
  trainingTitel: string;
  onClose: () => void;
}

const StarRating: React.FC<{
  value: number;
  onChange: (v: number) => void;
  labels: string[];
}> = ({ value, onChange, labels }) => {
  const [hovered, setHovered] = useState(0);
  const display = hovered || value;
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="focus:outline-none"
        >
          <Star
            className={`w-7 h-7 transition-colors ${
              star <= display ? 'fill-[#E5A425] text-[#E5A425]' : 'text-gray-300'
            }`}
          />
        </button>
      ))}
      {display > 0 && (
        <span className="ml-2 text-sm text-gray-500">{labels[display]}</span>
      )}
    </div>
  );
};

export const TrainingReviewModal: React.FC<Props> = ({ trainingId, trainingTitel, onClose }) => {
  const { t } = useTranslation('pages');
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const isEmployee = user?.role === 'employer_member' || user?.role === 'opdrachtgever';
  const isMedewerker = user?.role === 'medewerker';
  // As 2 wisselt per cursus-rij: toepasbaarheid (rij 1–3) of welzijn (rij 4, passie/hobby)
  const { data: vragen } = useQuery({ queryKey: ['review-vragen', trainingId], queryFn: () => getReviewVragen(trainingId) });
  const as2 = vragen?.as2_variant ?? 'toepasbaarheid';

  const starLabels = [
    '',
    t('trainingReview.stars.poor'),
    t('trainingReview.stars.moderate'),
    t('trainingReview.stars.sufficient'),
    t('trainingReview.stars.good'),
    t('trainingReview.stars.excellent'),
  ];

  // 3-Assen Review Model (zelfde vraagtekst voor iedere doelgroep)
  const AS1: Question = { key: 'expertise', label: t('trainingReview.assen.inhoud.label'), description: t('trainingReview.assen.inhoud.description') };
  const AS2: Question = { key: 'toepasbaarheid', label: t(`trainingReview.assen.${as2}.label`), description: t(`trainingReview.assen.${as2}.description`) };
  const AS3: Question = { key: 'structuur', label: t('trainingReview.assen.structuur.label'), description: t('trainingReview.assen.structuur.description') };
  const vraag = (groep: 'professional' | 'employee', key: Question['key']): Question => ({
    key,
    label: t(`trainingReview.questions.${groep}.${key}.label`),
    description: t(`trainingReview.questions.${groep}.${key}.description`),
  });

  const questions: Question[] = isMedewerker
    ? [AS1, AS2, AS3, vraag('employee', 'aanbeveling')]
    : (() => {
        const groep = isEmployee ? 'employee' : 'professional';
        return [
          vraag(groep, 'relevantie'),
          AS1,
          AS2,
          AS3,
          vraag(groep, 'prijs_kwaliteit'),
          vraag(groep, 'algemene_ervaring'),
          vraag(groep, 'aanbeveling'),
        ];
      })();

  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [openComment, setOpenComment] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: () => {
      const antwoorden = Object.fromEntries(questions.map((q) => [q.key, ratings[q.key]]));
      return submitTrainingReview(trainingId, {
        ...(antwoorden as Omit<SubmitReviewData, 'open_comment'>),
        open_comment: openComment || undefined,
      });
    },
    onSuccess: () => {
      setSubmitted(true);
      queryClient.invalidateQueries({ queryKey: ['training-reviews', trainingId] });
      queryClient.invalidateQueries({ queryKey: ['my-training-review', trainingId] });
      queryClient.invalidateQueries({ queryKey: ['mijn-aanmeldingen'] });
      queryClient.invalidateQueries({ queryKey: ['medewerker-academy'] });
    },
  });

  const allRated = questions.every((q) => (ratings[q.key] ?? 0) > 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!allRated) return;
    mutation.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-[#0B1F3A] rounded-t-2xl px-6 py-5 flex items-start justify-between">
          <div>
            <h2 className="text-white font-bold text-lg">{t('trainingReview.headerTitle')}</h2>
            <p className="text-[#E5A425] text-sm mt-0.5">{trainingTitel}</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white ml-4 flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {submitted ? (
          <div className="p-10 text-center">
            <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('trainingReview.thankYouTitle')}</h3>
            <p className="text-gray-500 text-sm mb-6">{t('trainingReview.thankYouDesc')}</p>
            <button
              onClick={onClose}
              className="bg-[#0B1F3A] text-white px-8 py-2.5 rounded-lg font-medium hover:bg-[#1A3459] transition-colors"
            >
              {t('trainingReview.close')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {isMedewerker ? (
              <p className="text-gray-600 text-sm">{t('trainingReview.anoniemIntro')}</p>
            ) : (
              <p className="text-gray-600 text-sm">
                {t('trainingReview.introText')}{' '}
                <strong>{t('trainingReview.notAnonymous')}</strong>{' '}
                {t('trainingReview.introTextSuffix')}
              </p>
            )}

            {/* Star questions */}
            <div className="space-y-5">
              {questions.map((q, i) => (
                <div key={q.key} className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0B1F3A] text-white text-xs flex items-center justify-center font-bold">
                      {i + 1}
                    </span>
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">{q.label}</div>
                      <div className="text-gray-500 text-xs mt-0.5">{q.description}</div>
                    </div>
                  </div>
                  <div className="pl-9">
                    <StarRating
                      value={ratings[q.key] ?? 0}
                      onChange={(v) => setRatings((prev) => ({ ...prev, [q.key]: v }))}
                      labels={starLabels}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Open comment */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">
                {t('trainingReview.yourExperience')}{' '}
                <span className="font-normal text-gray-500">{t('trainingReview.optional')}</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">{t('trainingReview.tip')}</p>
              <textarea
                value={openComment}
                onChange={(e) => setOpenComment(e.target.value)}
                rows={4}
                maxLength={2000}
                placeholder={t('trainingReview.commentPlaceholder')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] resize-none"
              />
              <div className="text-right text-xs text-gray-400 mt-1">{openComment.length}/2000</div>
            </div>

            {mutation.isError && (
              <p className="text-sm text-red-600">
                {(mutation.error as any)?.response?.data?.message || t('trainingReview.error')}
              </p>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={!allRated || mutation.isPending}
                className="flex-1 bg-[#0B1F3A] text-white py-3 rounded-lg font-semibold hover:bg-[#1A3459] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {mutation.isPending ? t('trainingReview.submitting') : t('trainingReview.submit')}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-3 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
              >
                {t('trainingReview.cancel')}
              </button>
            </div>
            {!allRated && (
              <p className="text-xs text-center text-gray-400">{t('trainingReview.allQuestionsRequired')}</p>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default TrainingReviewModal;
